/**
 * InvalidPathsPanel
 *
 * Displays invalid paths with reasons
 */

import React from 'react';
import { Box, Typography, Paper, IconButton, Chip } from '@mui/material';
import { Close as CloseIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import type { InvalidPath } from '../../shared/pathRouter';

interface InvalidPathsPanelProps {
  invalidPaths: InvalidPath[];
  onRemove?: (path: string) => void;
}

export const InvalidPathsPanel: React.FC<InvalidPathsPanelProps> = ({
  invalidPaths,
  onRemove,
}) => {
  if (invalidPaths.length === 0) {
    return null;
  }

  const handleCopy = (path: string) => {
    navigator.clipboard.writeText(path);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mt: 2,
        backgroundColor: 'warning.light',
        color: 'warning.contrastText',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        ⚠️ Invalid Paths ({invalidPaths.length})
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {invalidPaths.map((item, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {item.path}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Reason: {item.reason}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={() => handleCopy(item.path)} title="Copy path">
                  <CopyIcon fontSize="small" />
                </IconButton>
                {onRemove && (
                  <IconButton size="small" onClick={() => onRemove(item.path)} title="Remove">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};
